// src/connections/WebSocketContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { getAuthToken } from '../auth';

const WebSocketContext = createContext(null);

/* -------------------------------------------------------------
   Auth / reconnect constants
   ------------------------------------------------------------- */
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 5_000; // 5 s
const CONNECTION_TIMEOUT = 15_000; // 15 s - connection timeout

/* -------------------------------------------------------------
   Provider
   ------------------------------------------------------------- */
export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  
  // Debug isConnected state changes
  useEffect(() => {
    console.log('[WS] isConnected state changed to:', isConnected);
  }, [isConnected]);
  
  // Debug WebSocket state
  useEffect(() => {
    const sock = wsRef.current;
    console.log('[WS] WebSocket state debug:', {
      isConnected,
      hasSocket: !!sock,
      readyState: sock?.readyState,
      isOpen: sock?.readyState === WebSocket.OPEN
    });
  }, [isConnected]);
  
  // New state for different message types
  const [formatTranscription, setFormatTranscription] = useState('');
  const [rawTranscription, setRawTranscription] = useState('');
  const [interimTranscription, setInterimTranscription] = useState('');
  const [objectives, setObjectives] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [questionList, setQuestionList] = useState(null);
  const [actionItems, setActionItems] = useState(null);
  const [emphasisPoints, setEmphasisPoints] = useState(null);
  const [sessionSentiment, setSessionSentiment] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [meetingData, setMeetingData] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [meetingForceClosed, setMeetingForceClosed] = useState(null);
  const [meetingTimeline, setMeetingTimeline] = useState(null);
  const [isClearingData, setIsClearingData] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const mountedRef = useRef(true);
  const manuallyDisconnectedRef = useRef(false);
  const meetingDataCallbackRef = useRef(null);
  const currentInstanceIdRef = useRef(null);
  const currentRoleRef = useRef('recorder');



  /* ---------- helper : clean current socket ---------- */
  const cleanup = useCallback(() => {
    const sock = wsRef.current;
    if (sock) {
      sock.onopen = sock.onclose = sock.onerror = sock.onmessage = null;
      if (sock.readyState === WebSocket.OPEN) sock.close();
    }
    wsRef.current = null;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

    /* ---------- connect ---------- */
  const connect = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Add a timeout to the entire connection process
      const connectionTimeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, CONNECTION_TIMEOUT);
      if (!mountedRef.current) {
        reject(new Error('Component not mounted'));
        return;
      }
      if (isConnectingRef.current) {
        reject(new Error('Already connecting'));
        return;
      }
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      // Check if auth token is available
      const authToken = getAuthToken();
      if (!authToken) {
        console.error('[WS] cannot connect - no auth token available');
        reject(new Error('No auth token available'));
        return;
      }

      isConnectingRef.current = true;
      manuallyDisconnectedRef.current = false;
      
      // Only cleanup if there's an existing socket that's not in OPEN state
      if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
        cleanup(); // reset any old socket
      }

      // Get WebSocket URL from environment variable - updated to use new pub/sub server
      const wsBaseUrl = window._env_?.REACT_APP_SERVER_WS || 'ws://localhost:3000';
      const wsUrl = `${wsBaseUrl}/api/meeting?token=${authToken}`;
      console.log('[WS] connecting to pub/sub system →', wsUrl);

      try {
        const socket = new WebSocket(wsUrl);
        socket.binaryType = 'arraybuffer'; // 🔑 ensure we receive raw buffers
        wsRef.current = socket;

      /* ----- onopen ----- */
      socket.onopen = () => {
        if (!mountedRef.current) return;
        console.log('[WS] connected to pub/sub system');
        console.log('[WS] Setting isConnected to true');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        // Add a small delay to ensure the WebSocket is fully ready
        setTimeout(() => {
          clearTimeout(connectionTimeout);
          console.log('[WS] WebSocket connection established and ready');
          
          resolve(); // Resolve the promise when connected
        }, 100);
      };

      /* ----- onmessage (text vs binary) ----- */
      socket.onmessage = (event) => {
        if (!mountedRef.current) return;

        try {
          // Binary frames ⇒ ignore here (handled elsewhere if ever needed)
          if (event.data instanceof ArrayBuffer) {
            console.log('[WS] binary frame', event.data.byteLength, 'bytes');
            return;
          }

          // Text frames ⇒ JSON payloads
          let data;
          try {
            data = JSON.parse(event.data);
          } catch (parseError) {
            console.warn('[WS] non-JSON text frame', event.data, parseError);
            return;
          }

                  /* --- handle new message types --- */
        switch (data.type) {
          case 'connection':
            setConnectionStatus(data);
            console.log('[WS] connection status updated:', data);
            break;
            
          case 'pong':
          case 'heartbeat':
            // Handle heartbeat messages from server
            // Only log every 10th heartbeat to reduce console noise
            if (!window.heartbeatCount) window.heartbeatCount = 0;
            window.heartbeatCount++;
            if (window.heartbeatCount % 10 === 0) {
              console.log('[WS] heartbeat received (count:', window.heartbeatCount, ')');
            }
            break;
            
          case 'subscription_confirmed':
            console.log('[WS] subscription confirmed:', data);
            // Set meeting started state when subscription is confirmed
            break;
            
          case 'subscription':
            console.log('[WS] subscription message received:', data);
            // Handle subscription confirmation or status updates
            if (data.action === 'subscribed') {
              console.log('[WS] successfully subscribed to instance:', data.instanceId, 'as', data.role);
            }
            break;
            
          case 'test-pubsub':
            console.log('[WS] test pub/sub message received:', data);
            break;
            
          case 'test':
            console.log('[WS] test message received from server:', data);
            break;
            
          case 'meeting_force_closed':
            console.log('[WS] meeting_force_closed event received:', data);
            setMeetingForceClosed({
              instanceId: data.instanceId,
              reason: data.reason,
              expectedDurationSeconds: data.expectedDurationSeconds,
              actualDurationSeconds: data.actualDurationSeconds,
              bufferMinutes: data.bufferMinutes,
              timestamp: data.timestamp,
              message: data.message
            });
            break;
            
          case 'pubsub_event':
            // Handle pub/sub events from the new system
            console.log('[WS] pubsub_event received:', data);
            // The pub/sub event structure could be:
            // 1. {type: 'pubsub_event', topic: '...', data: {...}, timestamp: '...'}
            // 2. {type: 'pubsub_event', event: {topic: '...', data: {...}}, timestamp: '...'}
            let event;
            if (data.event) {
              // Structure 2: data.event exists
              event = data.event;
              console.log('[WS] Using data.event structure');
            } else {
              // Structure 1: data is the event
              event = data;
              console.log('[WS] Using data as event structure');
            }
            
            console.log('[WS] Processing pubsub event:', {
              topic: event.topic,
              hasData: !!event.data,
              dataKeys: event.data ? Object.keys(event.data) : null
            });
            
            switch (event.topic) {
                case 'transcription_update':
                  // Skip processing if we're in the middle of clearing data
                  if (isClearingData) {
                    console.log('[WS] Skipping transcription update - data is being cleared');
                    break;
                  }
                  
                  const transcriptionData = event.data || {};
                  // Use fullText for raw transcription (ignore 'text' property)
                  const rawText = transcriptionData.fullText || '';
                  setRawTranscription(rawText);
                  
                  // Only set formatted transcription if there's actual content
                  const formattedTranscriptionArray = transcriptionData.formattedTranscription || [];
                  if (formattedTranscriptionArray.length > 0) {
                    setFormatTranscription(formattedTranscriptionArray);
                  }
                  // Don't set empty array - let it remain null/empty so fallback works
                  
                  // Add debugging for pub/sub transcription data
                  console.log('[WS] Pub/sub transcription data processed:', {
                    rawText: rawText,
                    formattedArray: formattedTranscriptionArray,
                    hasRawText: !!rawText,
                    hasFormattedArray: formattedTranscriptionArray.length > 0,
                    eventData: event.data
                  });
                  break;
                  
                case 'formatted-transcription':
                  // Skip processing if we're in the middle of clearing data
                  if (isClearingData) {
                    console.log('[WS] Skipping formatted transcription update - data is being cleared');
                    break;
                  }
                  
                  const formattedTranscriptionData = event.data || {};
                  const legacyFormattedText = formattedTranscriptionData.text || formattedTranscriptionData.fullText || formattedTranscriptionData.content || '';
                  // Only set formatted transcription if there's actual content
                  if (legacyFormattedText && legacyFormattedText.trim()) {
                    setFormatTranscription(legacyFormattedText);
                  }
                  console.log('[WS] formatted transcription updated via pubsub:', {
                    text: legacyFormattedText,
                    type: typeof legacyFormattedText,
                    dataStructure: typeof formattedTranscriptionData,
                    hasText: !!formattedTranscriptionData.text,
                    hasFullText: !!formattedTranscriptionData.fullText
                  });
                  break;
                  
                case 'objectives_update':
                  const objectivesData = event.data || {};
                  const objectivesArray = objectivesData.objectives || [];
                  setObjectives({ 
                    objectivesAnalysis: { 
                      objectives: objectivesArray 
                    } 
                  });
                  break;
                  
                case 'suggestions_update':
                  const nextStepsData = event.data || {};
                  const nextStepsArray = nextStepsData.suggestions || nextStepsData.nextSteps || [];
                  setSuggestions({ 
                    nextSteps: { 
                      nextSteps: nextStepsArray 
                    } 
                  });
                  break;
                  
                case 'questions_update':
                  const questionsData = event.data || {};
                  const questionsArray = questionsData.questions || questionsData.questionsAndConcerns || [];
                  setQuestionList({ 
                    questionsAnalysis: { 
                      questionsAndConcerns: questionsArray 
                    } 
                  });
                  console.log('[WS] question list updated via pubsub:', {
                    count: questionsArray.length,
                    questions: questionsArray.map(q => q.content?.substring(0, 50) + '...'),
                    fullData: questionsArray,
                    dataStructure: typeof questionsData,
                    hasQuestions: !!questionsData.questions,
                    hasQuestionsAndConcerns: !!questionsData.questionsAndConcerns
                  });
                  break;
                  
                case 'action_items_update':
                  const actionItemsData = event.data || {};
                  const actionItemsArray = actionItemsData.actionItems || actionItemsData.items || [];
                  setActionItems({ 
                    actionItems: actionItemsArray 
                  });
                  console.log('[WS] action items updated via pubsub:', {
                    count: actionItemsArray.length,
                    items: actionItemsArray.map((item, index) => `${index + 1}. ${item.substring(0, 50)}...`),
                    dataStructure: typeof actionItemsData,
                    hasActionItems: !!actionItemsData.actionItems,
                    hasItems: !!actionItemsData.items
                  });
                  break;
                  
                case 'emphasis_points_update':
                  const emphasisPointsData = event.data || {};
                  const emphasisPointsArray = emphasisPointsData.emphasisPoints || emphasisPointsData.points || [];
                  setEmphasisPoints({ 
                    emphasisPoints: emphasisPointsArray 
                  });
                  console.log('[WS] emphasis points updated via pubsub:', {
                    count: emphasisPointsArray.length,
                    points: emphasisPointsArray.map((point, index) => `${index + 1}. ${point.substring(0, 50)}...`),
                    dataStructure: typeof emphasisPointsData,
                    hasEmphasisPoints: !!emphasisPointsData.emphasisPoints,
                    hasPoints: !!emphasisPointsData.points
                  });
                  break;
                  
                case 'stop-meeting':
                  console.log('[WS] stop-meeting event received via pubsub:', event);
                  // Set a special flag to indicate the meeting was stopped remotely
                  setServerError('MEETING_STOPPED_REMOTELY');
                  break;
                  
                case 'meeting_force_closed':
                  console.log('[WS] meeting_force_closed event received via pubsub:', event);
                  setMeetingForceClosed({
                    instanceId: event.data.instanceId,
                    reason: event.data.reason,
                    expectedDurationSeconds: event.data.expectedDurationSeconds,
                    actualDurationSeconds: event.data.actualDurationSeconds,
                    bufferMinutes: event.data.bufferMinutes,
                    timestamp: event.data.timestamp,
                    message: event.data.message
                  });
                  break;
                  
                case 'interim-transcription':
                  console.log('[WS] interim-transcription event received via pubsub:', event);
                  const interimData = event.data || {};
                  const interimText = interimData.data || interimData.text || interimData.content || '';
                  setInterimTranscription(interimText);
                  console.log('[WS] interim transcription updated:', {
                    text: interimText,
                    hasText: !!interimText,
                    dataStructure: typeof interimData,
                    availableKeys: Object.keys(interimData)
                  });
                  break;
                  
                case 'interim-transcription-clear':
                  console.log('[WS] interim-transcription-clear event received via pubsub:', event);
                  setInterimTranscription('');
                  console.log('[WS] interim transcription cleared');
                  break;
                  
                case 'meeting_timeline':
                  console.log('[WS] meeting_timeline event received via pubsub:', event);
                  const timelineData = event.data || {};
                  setMeetingTimeline(timelineData);
                  console.log('[WS] meeting timeline updated:', {
                    hasData: !!timelineData,
                    minutesCount: timelineData?.data?.minutes?.length || 0,
                    averageWordsPerMinute: timelineData?.data?.averageWordsPerMinute
                  });
                  break;
                  
                default:
                  console.log('[WS] unknown pubsub event topic:', event.topic, event);
                  break;
              }
            break;
            
          // Keep backward compatibility for direct messages (if any)
          case 'transcription_update':
            console.log('[WS] received direct transcription_update:', data);
            const directTranscriptionData = data || {};
            const directRawText = directTranscriptionData.fullText || '';
            setRawTranscription(directRawText);
            
            const directFormattedTranscriptionArray = directTranscriptionData.formattedTranscription || [];
            if (directFormattedTranscriptionArray.length > 0) {
              setFormatTranscription(directFormattedTranscriptionArray);
            }
            
            // Add debugging for transcription data
            console.log('[WS] Direct transcription data processed:', {
              rawText: directRawText,
              formattedArray: directFormattedTranscriptionArray,
              hasRawText: !!directRawText,
              hasFormattedArray: directFormattedTranscriptionArray.length > 0
            });
            break;
            
          case 'formatted-transcription':
            console.log('[WS] received direct formatted-transcription:', data);
            const directFormattedText = data.text || data.fullText || data.content || '';
            if (directFormattedText && directFormattedText.trim()) {
              setFormatTranscription(directFormattedText);
            }
            break;
            
          case 'objectives_update':
            console.log('[WS] received direct objectives_update:', data);
            const directObjectivesArray = data.objectives || [];
            setObjectives({ 
              objectivesAnalysis: { 
                objectives: directObjectivesArray 
              } 
            });
            break;
            
          case 'suggestions_update':
            console.log('[WS] received direct suggestions_update:', data);
            const directNextStepsArray = data.suggestions || data.nextSteps || [];
            setSuggestions({ 
              nextSteps: { 
                nextSteps: directNextStepsArray 
              } 
            });
            break;
            
          case 'questions_update':
            console.log('[WS] received direct questions_update:', data);
            const directQuestionsArray = data.questions || data.questionsAndConcerns || [];
            setQuestionList({ 
              questionsAnalysis: { 
                questionsAndConcerns: directQuestionsArray 
              } 
            });
            break;
            
          case 'action_items_update':
            console.log('[WS] received direct action_items_update:', data);
            const directActionItemsArray = data.actionItems || data.items || [];
            setActionItems({ 
              actionItems: directActionItemsArray 
            });
            break;
            
          case 'emphasis_points_update':
            console.log('[WS] received direct emphasis_points_update:', data);
            const directEmphasisPointsArray = data.emphasisPoints || data.points || [];
            setEmphasisPoints({ 
              emphasisPoints: directEmphasisPointsArray 
            });
            break;
            
          case 'session-sentiment':
            setSessionSentiment(data);
            console.log('[WS] session sentiment updated');
            break;
            

            
          case 'adhoc-meeting-started':
            setMeetingData(data.meetingData);
            console.log('[WS] ad-hoc meeting started:', data);
            break;
            
          case 'guest-meeting-joined':
            setMeetingData(data.meetingData);
            console.log('[WS] guest meeting joined:', data);
            break;
            
          case 'error':
            // Check if this is a Whisper API error (expected during audio processing)
            if (data.message && data.message.includes('Whisper API Error')) {
              console.warn('[WS] Whisper API error (expected):', data.message);
              // Don't set serverError for expected Whisper API errors
            } else {
              setServerError(data.message || 'Server error occurred');
              console.error('[WS] server error:', data.message);
            }
            break;
            
          default:
            console.log('[WS] unknown message type:', data.type, data);
            // Log the full message structure for debugging
            console.log('[WS] Full message structure:', {
              type: data.type,
              hasData: !!data.data,
              hasEvent: !!data.event,
              keys: Object.keys(data),
              dataKeys: data.data ? Object.keys(data.data) : null
            });
            
            // Log the actual message content for debugging
            console.log('[WS] Raw message content:', JSON.stringify(data, null, 2));
            break;
        }
        } catch (messageError) {
          console.error('[WS] Error processing message:', messageError);
          // Don't close the connection for message processing errors
          // Just log the error and continue
        }
      };

      /* ----- onerror ----- */
      socket.onerror = (err) => {
        if (!mountedRef.current) return;
        console.error('[WS] WebSocket error:', err);
        console.error('[WS] Error details:', {
          error: err,
          readyState: socket.readyState,
          url: socket.url,
          timestamp: new Date().toISOString()
        });
        
        // Log additional error context
        console.error('[WS] WebSocket readyState at error:', socket.readyState);
        console.error('[WS] WebSocket bufferedAmount at error:', socket.bufferedAmount);
        
        clearTimeout(connectionTimeout);
        reject(err); // Reject the promise on error
      };

      /* ----- onclose ----- */
      socket.onclose = (evt) => {
        if (!mountedRef.current) return;
        
        console.log('[WS] WebSocket closed:', {
          code: evt.code,
          reason: evt.reason,
          wasClean: evt.wasClean,
          timestamp: new Date().toISOString(),
          reconnectAttempts: reconnectAttemptsRef.current,
          manuallyDisconnected: manuallyDisconnectedRef.current
        });
        
        // Log abnormal closures for debugging
        if (evt.code === 1006) {
          console.warn('[WS] Abnormal closure detected (code 1006)');
        }
        
        setIsConnected(false);
        isConnectingRef.current = false;

        // Auto-reconnect unless it was manual or we hit the cap
        if (
          !manuallyDisconnectedRef.current &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          reconnectAttemptsRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY);
          console.log(
            `[WS] reconnect attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS}`
          );
        } else {
          cleanup();
        }
      };
    } catch (err) {
      console.error('[WS] creation failed', err);
      isConnectingRef.current = false;
      cleanup();
      clearTimeout(connectionTimeout);
      reject(err); // Reject the promise on creation error
    }
    }); // Close the Promise
  }, [cleanup]);

  /* ---------- mount / unmount ---------- */
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  /** Send raw audio (ArrayBuffer / Uint8Array / Buffer) */
  const sendAudioData = (audioBuf) => {
    const sock = wsRef.current;
    if (sock?.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    
    try {
      sock.send(audioBuf); // 🔑 no JSON stringify
    } catch (err) {
      console.error('[WS] sendAudioData failed', err);
      throw err;
    }
  };

  /** Send JSON message */
  const sendMessage = (type, data = {}) => {
    const sock = wsRef.current;
    if (sock?.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    try {
      let payload;
      
      // Special handling for start-meeting to put instance_id and consumer_type at top level
      if (type === 'start-meeting') {
        if (data.instance_id) {
          payload = {
            type,
            instance_id: data.instance_id,
            consumer_type: data.consumerType || 'recorder'
          };
        } else {
          payload = {
            type,
            data,
            timestamp: new Date().toISOString(),
          };
        }
      } else {
        payload = {
          type,
          data,
          timestamp: new Date().toISOString(),
        };
      }
      
      sock.send(JSON.stringify(payload));
      console.log('[WS] message sent:', type, data);
    } catch (err) {
      console.error('[WS] sendMessage failed', err);
      throw err;
    }
  };

  /** Send test message to verify backend communication */
  const sendTestMessage = () => {
    const sock = wsRef.current;
    if (sock?.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    
    try {
      const testPayload = {
        type: 'test',
        message: 'Hello from frontend',
        timestamp: new Date().toISOString(),
        instance_id: currentInstanceIdRef.current,
        role: currentRoleRef.current
      };
      
      sock.send(JSON.stringify(testPayload));
      console.log('[WS] test message sent');
    } catch (err) {
      console.error('[WS] sendTestMessage failed', err);
      throw err;
    }
  };



  /** Subscribe to instance updates (new pub/sub system) */
  const subscribeToInstance = (instanceId, role = 'viewer') => {
    const sock = wsRef.current;
    if (sock?.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    
    try {
      const payload = {
        type: 'subscription',
        action: 'subscribe',
        instance_id: instanceId,
        role: role,
        sendLastMessage: true,
        topics: [
          'transcription_update',
          'formatted-transcription', 
          'objectives_update', 
          'suggestions_update', 
          'questions_update', 
          'action_items_update', 
          'emphasis_points_update',
          'sentiment_update',
          'meeting_started',
          'participant_joined',
          'raw-transcription',
          'stop-meeting',
          'meeting_force_closed',
          'interim-transcription',
          'interim-transcription-clear',
          'meeting_timeline'
        ]
      };
      
      sock.send(JSON.stringify(payload));
      currentInstanceIdRef.current = instanceId;
      currentRoleRef.current = role;
      console.log('[WS] subscribed to instance:', instanceId, 'as', role);
    } catch (err) {
      console.error('[WS] subscribeToInstance failed', err);
      throw err;
    }
  };

  /** Unsubscribe from instance updates */
  const unsubscribeFromInstance = () => {
    const sock = wsRef.current;
    if (sock?.readyState !== WebSocket.OPEN || !currentInstanceIdRef.current) {
      return;
    }
    
    try {
      const payload = {
        type: 'subscription',
        action: 'unsubscribe',
        instance_id: currentInstanceIdRef.current,
        role: currentRoleRef.current
      };
      
      sock.send(JSON.stringify(payload));
      console.log('[WS] unsubscribed from instance:', currentInstanceIdRef.current);
      currentInstanceIdRef.current = null;
    } catch (err) {
      console.error('[WS] unsubscribeFromInstance failed', err);
      throw err;
    }
  };

  const disconnect = useCallback(() => {
    manuallyDisconnectedRef.current = true;
    // Unsubscribe before disconnecting
    if (currentInstanceIdRef.current) {
      try {
        unsubscribeFromInstance();
      } catch (err) {
        console.warn('[WS] failed to unsubscribe during disconnect:', err);
      }
    }
    cleanup();
    setIsConnected(false);
  }, [cleanup]);

  const clearServerError = useCallback(() => {
    setServerError(null);
  }, []);



  // Check WebSocket ready state
  const getWebSocketState = useCallback(() => {
    const sock = wsRef.current;
    const actualConnected = sock?.readyState === WebSocket.OPEN;
    return {
      isConnected: actualConnected, // Use actual WebSocket state instead of React state
      hasSocket: !!sock,
      readyState: sock?.readyState,
      isOpen: actualConnected
    };
  }, [isConnected]);

  // Setters for completed meeting data
  const setCompletedMeetingData = useCallback((data) => {
    if (data.transcript) setRawTranscription(data.transcript);
    if (data.objectives) setObjectives(data.objectives);
    if (data.suggestions) setSuggestions(data.suggestions);
    if (data.questionList) setQuestionList(data.questionList);
    if (data.actionItems) setActionItems(data.actionItems);
    if (data.emphasisPoints) setEmphasisPoints(data.emphasisPoints);
    if (data.sessionSentiment) setSessionSentiment(data.sessionSentiment);
    if (data.formatTranscription) setFormatTranscription(data.formatTranscription);
    if (data.timeline) setMeetingTimeline(data.timeline);
  }, []);

  // Clear all WebSocket data (for new meetings)
  const clearWebSocketData = useCallback(() => {
    console.log('[WS] Clearing all WebSocket data for new meeting');
    setIsClearingData(true);
    setRawTranscription('');
    setFormatTranscription('');
    setInterimTranscription('');
    setObjectives(null);
    setSuggestions(null);
    setQuestionList(null);
    setActionItems(null);
    setEmphasisPoints(null);
    setSessionSentiment(null);
    setMeetingData(null);
    setServerError(null);
    setMeetingForceClosed(null);
    setMeetingTimeline(null);
    
    // Reset the clearing flag after a short delay to allow state updates to complete
    setTimeout(() => {
      setIsClearingData(false);
    }, 100);
  }, []);

  /* ---------- provider obj ---------- */
  const value = {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN, // Use actual WebSocket state
    // New data from WebSocket
    formatTranscription,
    rawTranscription,
    interimTranscription,
    objectives,
    suggestions,
    questionList,
    actionItems,
    emphasisPoints,
    sessionSentiment,
    connectionStatus,
    meetingData,
    serverError,
    meetingForceClosed,
    meetingTimeline,
    // Functions
    sendAudioData,
    sendMessage,
    sendTestMessage,
    subscribeToInstance,
    unsubscribeFromInstance,
    disconnect,
    connect,
    clearServerError,
    setCompletedMeetingData,
    clearWebSocketData,
    getWebSocketState,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/* -------------------------------------------------------------
   Hook
   ------------------------------------------------------------- */
export const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocket must be used within WebSocketProvider');
  return ctx;
};

